// HOLD, DON'T FAIL (2026-09-29): a task whose attempt ladder was spent on an environment
// fault that never graded the change stays `pending` on `TaskHoldReason.EnvFaultExhausted`
// with ONE open blocker. This module is the pure half — the window, the blocker a hold
// raises and how it is keyed; the write is `holdExhaustedTask` (`@developerz/db`), and the
// kinds that hold are `taskFailureHoldsOnExhaustion` (`./failure-budget`).
//
// THE INCIDENT: `tsk_1be837ae…` (prod, 2026-09-29) spent three attempts — two `verify_failed`,
// then `gate_unrunnable` when the repo's gate exited 75 on `cargo-deny not found`, a tool
// missing from every box. The third was the last rung, so the task went `failed` and six
// dependents went `failed` with it, never claimed, stalling the plan over a box nobody had
// provisioned.

import type { AccountId } from '../../value-objects/ids';
import {
  BLOCKER_DEDUPE_KEY_MAX,
  BLOCKER_DETAIL_MAX,
  BLOCKER_EXPIRY_MS,
  BLOCKER_SUMMARY_MAX,
  type BlockerDraft,
  BlockerKind,
} from '../blocker';
import type { TaskFailureKind } from './failure-kind';

/**
 * How long ONE hold lasts (`tasks.hold_until`): the life of the blocker it raised
 * (`BLOCKER_EXPIRY_MS`), so the two lapse together and the task is never held with nobody
 * asked, nor asked about after it moved on its own.
 *
 * NOT a retry clock. The wake is the operator (`task redispatch`, once the box is fixed).
 * The window is the fail-safe against a hold nobody reads: past it, ONE run is let through
 * with the ladder still spent, so a fleet that is still broken holds it again on the first
 * refusal — one paid session per window, where a short clock would re-bill a full model
 * session (35 minutes, plus two 20-minute gate runs, on the incident's third attempt) on
 * every lapse.
 */
export const ENV_FAULT_HOLD_MS = BLOCKER_EXPIRY_MS;

/** How much of the gate's refusal the blocker quotes: the END of its output, where a gate says why. */
const REFUSAL_ROOM_MIN = 120;

/**
 * Every blocker a hold raises for `taskId` starts with this, so the hold step can find
 * the task's OPEN one (a re-hold after the window reuses it) and a redispatch can
 * withdraw it, without a column of its own. `-`-joined like the repo-env key.
 */
export function envFaultHoldKeyPrefix(taskId: string): string {
  return `env-fault-hold-${taskId}-`;
}

/**
 * The key of the blocker ONE hold raises: the prefix plus the row's fence generation at the
 * hold, which every re-pend and every claim bumps — so an episode after an answered,
 * withdrawn or expired blocker gets a fresh ask, never the closed one back.
 */
export function envFaultHoldKey(taskId: string, leaseEpoch: number): string {
  return `${envFaultHoldKeyPrefix(taskId)}${String(leaseEpoch)}`.slice(0, BLOCKER_DEDUPE_KEY_MAX);
}

/** Was this blocker raised by a hold on `taskId` — so answering it releases the task instead of filing work? */
export function isEnvFaultHoldBlocker(taskId: string, dedupeKey: string): boolean {
  return dedupeKey.startsWith(envFaultHoldKeyPrefix(taskId));
}

/** The end of a refusal, whitespace flattened, capped to `max` from the END. */
function refusalEnd(text: string, max: number): string {
  const flat = text.replaceAll(/\s+/g, ' ').trim();
  return flat.length <= max ? flat : `…${flat.slice(-(max - 1))}`;
}

/** What one hold knows when it raises its blocker. */
export type EnvFaultHoldFacts = {
  readonly accountId: AccountId;
  readonly taskId: string;
  readonly repoFullName: string | null;
  /** The run whose refusal ended the ladder, when the row still names it. */
  readonly runId: string | null;
  /** The box that sent the last refusal. */
  readonly runnerId: string | null;
  readonly failureKind: TaskFailureKind;
  /** Attempts the row has spent. */
  readonly attempt: number;
  readonly leaseEpoch: number;
  /**
   * The gate's own words: the recorded tail of the no-verdict gate run when there is one,
   * else the box's error message. `''` when neither exists.
   */
  readonly refusal: string;
};

/**
 * The blocker a hold raises. Pure. `decision_needed`, the kind the repo-env fault takes for
 * the same shape of ask: nothing a box does on its own changes the answer, a person must.
 *
 * THE DETAIL NAMES THE RELEASE, because the blocker plane's default does not apply:
 * answering a blocker files a NEW raw task for triage (`answerBlocker`), and this ask is
 * not for new work — it is for the held task to run again once the box is fixed. So an
 * answer to this blocker is the operator redispatch of the held task, with the answer as its
 * guidance (`apps/api` `deps/blocker-hold-release.ts`), and `task redispatch` does the same.
 */
export function envFaultHoldBlockerDraft(facts: EnvFaultHoldFacts): BlockerDraft {
  const where = facts.repoFullName === null ? '' : `${facts.repoFullName}: `;
  const summary =
    `${where}task ${facts.taskId} held — ${facts.failureKind} on every attempt (${String(facts.attempt)}); the change was never graded`.slice(
      0,
      BLOCKER_SUMMARY_MAX,
    );
  const how = `Held, not failed; dependents wait. Fix the box, then answer this blocker or run \`task redispatch ${facts.taskId}\`: either releases it with fresh attempts. Refusal: `;
  const room = Math.max(REFUSAL_ROOM_MIN, BLOCKER_DETAIL_MAX - how.length);
  const said =
    facts.refusal.trim() === '' ? '(none recorded — see `task run_tail`)' : facts.refusal;
  const detail = `${how}${refusalEnd(said, room)}`.slice(0, BLOCKER_DETAIL_MAX);
  return {
    accountId: facts.accountId,
    repoFullName: facts.repoFullName,
    runId: facts.runId,
    taskId: facts.taskId as BlockerDraft['taskId'],
    runnerId: facts.runnerId,
    kind: BlockerKind.DecisionNeeded,
    summary,
    detail,
    dedupeKey: envFaultHoldKey(facts.taskId, facts.leaseEpoch),
  };
}

/**
 * WHAT CLEARS `TaskStallKind.EnvFaultExhausted` — `task stalls`' sentence. The task is
 * held, not dead: its dependents and its plan wait on it.
 */
export const ENV_FAULT_HOLD_REMEDY =
  "every attempt this task had ended on an environment fault that never graded the change (the repo's gate could not run or refused to grade on the boxes it reached), so it is HELD rather than failed and its dependents wait. Its open blocker quotes the gate's refusal: fix that on the boxes (install the missing tool, restore the service), then `task redispatch`, or `blocker answer` on that blocker, runs it again with fresh attempts and withdraws the blocker. Left alone, one run is let through when the hold lapses";
